import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, CalendarClock, Plus, Save, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAppearance } from "@/context/AppearanceContext";
import { ImageCropUpload } from "@/components/admin/appearance/ImageCropUpload";

const idOf = (url) => (url ? url.split("/").pop() : null);
const toInput = (iso) => (iso ? new Date(iso).toLocaleString("sv-SE", { timeZone: "Asia/Jakarta" }).slice(0, 16).replace(" ", "T") : "");
const fromInput = (v) => (v ? `${v}:00+07:00` : null);
const fmtWib = (iso) => `${new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} WIB`;
const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };

export const scheduleStatus = (start, end) => {
  const now = Date.now();
  if (start && new Date(start).getTime() > now) return ["Terjadwal", "bg-sky-500/15 text-sky-300"];
  if (end && new Date(end).getTime() <= now) return ["Berakhir", "bg-zinc-500/20 text-zinc-400"];
  return [start || end ? "Tayang" : "Selalu tayang", "bg-emerald-500/15 text-emerald-300"];
};

const StatusBadge = ({ start, end, testid }) => {
  const [label, cls] = scheduleStatus(start, end);
  return <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={testid}>{label}</span>;
};

const ScheduleFields = ({ start, end, onChange, disabled, testid }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-400" data-testid={testid}>
    <CalendarClock size={13} className="text-[#e5b80b]" />
    <span>Mulai</span>
    <input type="datetime-local" value={toInput(start)} onChange={(e) => onChange({ start: fromInput(e.target.value), end })} disabled={disabled} className="rk-input !w-auto !py-1 text-xs [color-scheme:dark]" data-testid={`${testid}-start`} />
    <span>Berakhir</span>
    <input type="datetime-local" value={toInput(end)} onChange={(e) => onChange({ start, end: fromInput(e.target.value) })} disabled={disabled} className="rk-input !w-auto !py-1 text-xs [color-scheme:dark]" data-testid={`${testid}-end`} />
    <span className="text-zinc-600">WIB · kosong = selalu tayang</span>
    {!disabled && (start || end) ? (
      <button type="button" onClick={() => onChange({ start: null, end: null })} className="rounded px-2 py-0.5 text-rose-400 hover:bg-rose-500/10" data-testid={`${testid}-clear`}>Hapus jadwal</button>
    ) : null}
    {start || end ? <span className="w-full pl-5 text-[11px] text-zinc-500" data-testid={`${testid}-summary`}>{start ? fmtWib(start) : "Sekarang"} → {end ? fmtWib(end) : "tanpa batas"}</span> : null}
  </div>
);

export const useAdminAppearance = () => {
  const { reload: reloadPublic } = useAppearance();
  const [data, setData] = useState(null);
  const reload = useCallback(async () => {
    const { data: d } = await api.get("/admin/appearance");
    setData(d);
    reloadPublic();
    return d;
  }, [reloadPublic]);
  useEffect(() => {
    reload().catch((e) => toast.error(errMsg(e)));
  }, [reload]);
  return [data, reload];
};

const CardImages = ({ a, canManage, reload }) => {
  const save = async (game, patch) => {
    const cur = (g) => ({ image: idOf(a.cards[g].image), icon: idOf(a.cards[g].icon) });
    try {
      await api.put("/admin/appearance/cards", { royal_dream: cur("royal_dream"), higgs_domino: cur("higgs_domino"), [game]: { ...cur(game), ...patch } });
      await reload();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan"));
      throw err;
    }
  };
  return (
    <div className="space-y-3" data-testid="card-images">
      <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400">Gambar Kartu Game (Beranda)</h3>
      <div className="grid gap-4 md:grid-cols-2">
        {Object.entries(GAMES).map(([g, n]) => (
          <div key={g} className="grid grid-cols-[1.4fr_1fr] gap-3">
            <ImageCropUpload kind="card_image" title={`Kartu ${n}`} guide="800 × 840 px" formats="WebP, JPG, PNG" aspect={800 / 840} outW={800} outH={840}
              current={a.cards[g].image} canManage={canManage} testid={`img-card-${g}`} onDone={(d) => save(g, { image: d.id })} onRemove={() => save(g, { image: null })} />
            <ImageCropUpload kind="card_icon" title={`Ikon ${n}`} guide="256 × 256 px" formats="PNG, WebP" aspect={1} outW={256} outH={256}
              current={a.cards[g].icon} canManage={canManage} testid={`img-icon-${g}`} onDone={(d) => save(g, { icon: d.id })} onRemove={() => save(g, { icon: null })} />
          </div>
        ))}
      </div>
    </div>
  );
};

const PromoBox = ({ a, canManage, save }) => {
  const [link, setLink] = useState(a.promo_link);
  const [sched, setSched] = useState({ start: a.promo_start, end: a.promo_end });
  return (
    <div className="space-y-2">
      <ImageCropUpload kind="promo" title="Banner Promo Persegi" guide="1080 × 1080 px" formats="WebP, JPG, PNG" aspect={1} outW={1080} outH={1080}
        current={a.promo} canManage={canManage} testid="img-promo" onDone={(d) => save({ promo: d.id })} onRemove={() => save({ promo: null })} />
      {a.promo ? (
        <div className="space-y-2 rounded-xl border border-white/5 bg-[#0d0d12] p-3">
          <div className="flex items-center gap-2 text-xs text-zinc-400">Status <StatusBadge start={a.promo_start} end={a.promo_end} testid="promo-status" /></div>
          <ScheduleFields start={sched.start} end={sched.end} onChange={setSched} disabled={!canManage} testid="promo-schedule" />
          {canManage ? (
            <div className="flex gap-2">
              <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Link promo (opsional): https://… atau /bongkar" className="rk-input !py-2 text-xs" data-testid="promo-link-input" />
              <button onClick={() => save({ promo_link: link, promo_start: sched.start, promo_end: sched.end }).then(() => toast.success("Promo disimpan")).catch(() => {})} className="rk-btn-primary !px-3 text-xs" data-testid="promo-link-save"><Save size={13} /></button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const LogoSettings = ({ canManage }) => {
  const [a, reload] = useAdminAppearance();
  if (!a) return <p className="text-sm text-zinc-500">Memuat…</p>;
  const save = async (patch) => {
    const body = { logo_main: idOf(a.logo_main), logo_horizontal: idOf(a.logo_horizontal), promo: idOf(a.promo), promo_link: a.promo_link, promo_start: a.promo_start, promo_end: a.promo_end, ...patch };
    try {
      await api.put("/admin/appearance/logos", body);
      await reload();
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan"));
      throw err;
    }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3" data-testid="logo-settings">
        <ImageCropUpload kind="logo_main" title="Logo Utama" guide="512 × 512 px" formats="PNG, WebP" aspect={1} outW={512} outH={512}
          current={a.logo_main} canManage={canManage} testid="img-logo-main" onDone={(d) => save({ logo_main: d.id })} onRemove={() => save({ logo_main: null })} />
        <ImageCropUpload kind="logo_horizontal" title="Logo Horizontal (header)" guide="800 × 250 px" formats="PNG, WebP" aspect={800 / 250} outW={800} outH={250}
          current={a.logo_horizontal} canManage={canManage} testid="img-logo-horizontal" onDone={(d) => save({ logo_horizontal: d.id })} onRemove={() => save({ logo_horizontal: null })} />
        <PromoBox key={`${a.promo}-${a.promo_start}-${a.promo_end}`} a={a} canManage={canManage} save={save} />
      </div>
      <CardImages a={a} canManage={canManage} reload={reload} />
    </div>
  );
};

const BannerCard = ({ b, idx, total, canManage, update, move, remove }) => (
  <div className="rounded-2xl border border-white/5 bg-[#101017] p-4" data-testid={`banner-item-${idx}`}>
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="rounded bg-[#e5b80b]/15 px-2 py-0.5 text-[11px] font-bold text-[#e5b80b]">Slide {idx + 1}</span>
      <StatusBadge start={b.start_at} end={b.end_at} testid={`banner-status-${idx}`} />
      <input value={b.title} onChange={(e) => update({ title: e.target.value })} disabled={!canManage} maxLength={60} placeholder="Judul (opsional)" className="rk-input !w-48 !py-1.5 text-xs" data-testid={`banner-title-${idx}`} />
      <input value={b.link} onChange={(e) => update({ link: e.target.value })} disabled={!canManage} maxLength={300} placeholder="Link (opsional)" className="rk-input !w-56 !py-1.5 text-xs" data-testid={`banner-link-${idx}`} />
      {canManage ? (
        <div className="ml-auto flex gap-1">
          <button onClick={() => move(-1)} disabled={idx === 0} className="rk-btn-ghost !px-2 !py-1 disabled:opacity-30" aria-label="Naik" data-testid={`banner-up-${idx}`}><ArrowUp size={13} /></button>
          <button onClick={() => move(1)} disabled={idx === total - 1} className="rk-btn-ghost !px-2 !py-1 disabled:opacity-30" aria-label="Turun" data-testid={`banner-down-${idx}`}><ArrowDown size={13} /></button>
          <button onClick={remove} className="rk-btn-ghost !px-2 !py-1 text-rose-400" aria-label="Hapus" data-testid={`banner-remove-${idx}`}><Trash2 size={13} /></button>
        </div>
      ) : null}
    </div>
    <div className="mb-3"><ScheduleFields start={b.start_at} end={b.end_at} onChange={({ start, end }) => update({ start_at: start, end_at: end })} disabled={!canManage} testid={`banner-schedule-${idx}`} /></div>
    <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
      <ImageCropUpload kind="banner_desktop" title="Banner Desktop" guide="1920 × 600 px" formats="WebP, JPG, PNG" aspect={1920 / 600} outW={1920} outH={600}
        current={b.desktop_url} canManage={canManage} testid={`banner-desktop-${idx}`} onDone={(d) => update({ desktop: d.id, desktop_url: d.url })} />
      <ImageCropUpload kind="banner_mobile" title="Banner Mobile (opsional)" guide="1080 × 1350 px" formats="WebP, JPG, PNG" aspect={1080 / 1350} outW={1080} outH={1350}
        current={b.mobile_url} canManage={canManage} testid={`banner-mobile-${idx}`} onDone={(d) => update({ mobile: d.id, mobile_url: d.url })}
        onRemove={() => update({ mobile: null, mobile_url: null })} />
    </div>
  </div>
);

const pickBanner = ({ id, title, link, desktop, mobile, start_at, end_at }) => ({ id, title, link: (link || "").trim(), desktop, mobile, start_at: start_at || null, end_at: end_at || null });

const BannerList = ({ initial, canManage, reload }) => {
  const [list, setList] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setList((l) => l.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, d) => setList((l) => {
    const n = [...l];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    return n;
  });
  const add = () => setList((l) => [...l, { id: Math.random().toString(36).slice(2, 10), title: "", link: "", desktop: null, mobile: null, start_at: null, end_at: null }]);
  const save = async () => {
    if (list.some((b) => !b.desktop)) return toast.error("Setiap slide wajib punya banner desktop");
    setBusy(true);
    try {
      await api.put("/admin/appearance/banners", { banners: list.map(pickBanner) });
      const d = await reload();
      setList(d.banners);
      toast.success("Banner disimpan");
    } catch (err) {
      toast.error(errMsg(err, "Gagal menyimpan banner"));
    } finally {
      setBusy(false);
    }
  };
  const dirty = JSON.stringify(list.map(pickBanner)) !== JSON.stringify(initial.map(pickBanner));
  return (
    <div className="space-y-4" data-testid="banner-settings">
      <p className="text-xs text-zinc-500">Slide Beranda tampil sesuai jadwal (WIB). Jika tidak ada slide yang sedang tayang, banner bawaan JP STORE dipakai. Banner mobile tampil di layar HP (≤640px).</p>
      {list.map((b, i) => (
        <BannerCard key={b.id} b={b} idx={i} total={list.length} canManage={canManage} update={(p) => set(i, p)} move={(d) => move(i, d)} remove={() => setList((l) => l.filter((_, j) => j !== i))} />
      ))}
      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <button onClick={add} disabled={list.length >= 10} className="rk-btn-ghost inline-flex items-center gap-1 text-sm" data-testid="banner-add"><Plus size={14} /> Tambah Slide</button>
          <button onClick={save} disabled={busy} className="rk-btn-primary inline-flex items-center gap-2 text-sm" data-testid="banner-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan Banner
          </button>
          {dirty ? <span className="self-center text-xs font-semibold text-amber-400" data-testid="banner-unsaved">Ada perubahan yang belum disimpan</span> : null}
        </div>
      ) : null}
    </div>
  );
};

export const BannerSettings = ({ canManage }) => {
  const [a, reload] = useAdminAppearance();
  if (!a) return <p className="text-sm text-zinc-500">Memuat…</p>;
  return <BannerList initial={a.banners} canManage={canManage} reload={reload} />;
};
